import styled, { css, keyframes } from 'styled-components';
import { ISpinnerProps, spinnerVariants } from './spinner-props';
import { isIndeterminate } from '../progress-indicator/utils';

interface ISpinnerBaseProps extends ISpinnerProps {
    circumference: number;
    dashOffset: number;
}

const spinnerSize = { small: 22, medium: 40, large: 50 };

export const getSpinnerSize = (size: spinnerVariants = 'medium') => spinnerSize[size];

export const SpinnerWrapper = styled.div.attrs(() => ({
    className: String('ap-spinner-wrapper')
})) <Pick<ISpinnerProps, 'size'>>`
    position: relative;
    display: inline-block;
`;

const spinnerAnimation = keyframes`
    0% {
        transform: rotate(0deg);
    }
    25% {
        transform: rotate(90deg);
    }
    50%{
        transform: rotate(180deg);
    }
    75%{
        transform: rotate(270deg);
    }
    100% {
        transform: rotate(360deg);
    }
`;

const inDeterminateStyle = css`
    animation: ${spinnerAnimation} 1s cubic-bezier(1, 1, 1, 1) infinite;
`;

export const CircularSpinnerWrapper = styled.svg.attrs(() => ({
    className: String('ap-spinner-circularSpinner-wrapper')
})) <Pick<ISpinnerProps, 'size' | 'spinnerType'>>`
    width: ${({ size }) => spinnerSize[size!]}px;
    height: ${({ size }) => spinnerSize[size!]}px;
    display: block;
    ${({ spinnerType }) => (isIndeterminate(spinnerType) ? inDeterminateStyle : '')}
`;

export const CircularSpinner = styled.circle.attrs(() => ({
    className: String('ap-spinner-circularSpinner')
})) <Pick<ISpinnerProps, 'size'>>`
    fill: none;
    stroke: ${({ theme: { spinner: { colors } } }) => colors.spinnerBackground};
    stroke-width: ${({ size }) => (size === 'small' ? 6 : 10)}px;
`;

export const CircularSpinnerFill = styled.circle.attrs(() => ({
    className: String('ap-spinner-circularSpinnerFill')
})) <Pick<ISpinnerBaseProps, 'size' | 'progress' | 'spinnerType' | 'circumference' | 'dashOffset'>>`
    fill: none;
    stroke: ${({ theme: { spinner: { colors } } }) => colors.spinnerFill};
    stroke-width: ${({ size }) => (size === 'small' ? 6 : 10)}px;
    stroke-linecap: round;
    transform: rotate(-90deg);
    stroke-dasharray: ${({ circumference, progress }) => (progress! < 100 ? circumference : circumference + 30)};
    stroke-dashoffset: ${({ dashOffset }) => dashOffset};
`;

const circularSpinnerSmall = css`
    font-size: 12px;
    font-weight: bold;
    color: ${({ theme: { spinner: { colors } } }) => colors.spinnerFill};
    padding-top: ${({ theme: { semantics: { gap } } }) => gap.m};
`;

const percentageStyle = css<Pick<ISpinnerProps, 'size'>>`
    font-size: ${({ size }) => Number(size) / 3.5}px;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    text-align: center;
    font-weight: bold;
    color: ${({ theme: { spinner: { colors } } }) => colors.spinnerFill};
`;

export const ProgressPercentage = styled.span.attrs(() => ({
    className: String('ap-spinner-progressPercentage')
})) <Pick<ISpinnerProps, 'size'>>`
    ${({ size }) => (size === 'small' ? circularSpinnerSmall : percentageStyle)}
`;
